// Shared types for the Style page catalogs.
export type Gender = "all" | "men" | "women";

export interface CatalogEntry {
  id: string;
  label: string;
  image: string;
  prompt: string;
  /** Genders this entry is appropriate for. */
  genders: Gender[];
}

export interface OutfitLookV2 extends CatalogEntry {
  category:
    | "daily"
    | "date"
    | "formal"
    | "halloween"
    | "home"
    | "christmas"
    | "indian-festive"
    | "temple"
    | "saree"
    | "party";
  /** Optional short description shown in the preview sheet. */
  description?: string;
}

export interface SingleItemV2 extends CatalogEntry {
  type:
    | "top"
    | "bottom"
    | "outerwear"
    | "shoes"
    | "bag"
    | "watch"
    | "jewellery"
    | "accessory";
  description?: string;
}

export type VibeCategory =
  | "wild"
  | "pets"
  | "vehicles"
  | "classic-cars"
  | "supercars"
  | "bikes"
  | "studio"
  | "wonders"
  | "travel"
  | "city"
  | "nature";

export interface VibeEntry extends CatalogEntry {
  /** A vibe can change just the scene, just the outfit-vibe, or both. */
  defaultMode: "scene" | "outfit";
  /** Hint string appended when "scene only" is picked. */
  scenePrompt: string;
  /** Hint string used when "outfit + scene" is picked. */
  outfitPrompt: string;
  description?: string;
  /** Filter group on the Scenes tab. Optional — defaults to "nature" if missing. */
  category?: VibeCategory;
}

export const VIBE_CATEGORY_LABELS: Record<VibeCategory, { label: string; emoji: string }> = {
  wild: { label: "Wild", emoji: "🐅" },
  pets: { label: "Pets", emoji: "🐶" },
  vehicles: { label: "Vehicles", emoji: "🚗" },
  "classic-cars": { label: "Classic Cars", emoji: "🚙" },
  supercars: { label: "Supercars", emoji: "🏎️" },
  bikes: { label: "Bikes", emoji: "🏍️" },
  studio: { label: "Studio", emoji: "📸" },
  wonders: { label: "7 Wonders", emoji: "🏛️" },
  travel: { label: "Travel", emoji: "✈️" },
  city: { label: "City", emoji: "🌆" },
  nature: { label: "Nature", emoji: "🌿" },
};

export const OUTFIT_CATEGORY_LABELS: Record<OutfitLookV2["category"], { label: string; emoji: string }> = {
  daily: { label: "Daily", emoji: "☀️" },
  date: { label: "Date Night", emoji: "💖" },
  formal: { label: "Formal", emoji: "🎩" },
  halloween: { label: "Halloween", emoji: "🎃" },
  home: { label: "Home / Lounge", emoji: "🛋️" },
  christmas: { label: "Christmas", emoji: "🎄" },
  "indian-festive": { label: "Indian Festive", emoji: "🪔" },
  temple: { label: "Temple Wear", emoji: "🛕" },
  saree: { label: "Saree", emoji: "🥻" },
  party: { label: "Party", emoji: "🎉" },
};

export const ITEM_TYPE_LABELS: Record<SingleItemV2["type"], { label: string; emoji: string }> = {
  top: { label: "Tops", emoji: "👕" },
  bottom: { label: "Bottoms", emoji: "👖" },
  outerwear: { label: "Jackets", emoji: "🧥" },
  shoes: { label: "Shoes", emoji: "👟" },
  bag: { label: "Bags", emoji: "👜" },
  watch: { label: "Watches", emoji: "⌚" },
  jewellery: { label: "Jewellery", emoji: "💍" },
  accessory: { label: "Accessories", emoji: "🧢" },
};

export const GENDER_OPTIONS: Array<{ id: Gender; label: string; emoji: string }> = [
  { id: "all", label: "All", emoji: "✨" },
  { id: "men", label: "Men", emoji: "👨" },
  { id: "women", label: "Women", emoji: "👩" },
];
